// useCreateTrip — submit/create flow + QuickSetup handoff hook for ManualTripCreator.
//
// Extracted VERBATIM from `components/ManualTripCreator.js` (behavior-preserving
// refactor, issue #240). Owns the create-path state the source held inline:
//   - `isCreating`     (in-flight submit flag that disables the form)
//   - `showQuickSetup` (post-create QuickSetupPrompt toggle)
//   - `createdTrip`    (the saved trip handed to QuickSetup / onTripCreated)
//
// `handleSubmit(e)` preserves the source's gates, order, args, and counts EXACTLY:
//   1. `e.preventDefault()`.
//   2. VALIDATION GATE — `validateForm(formData)`; on a truthy message set it as
//      the error and RETURN (no service / network call).
//   3. AUTH GATE — `getUserId(user)`; when it resolves no id, `console.error`
//      the same '❌ User not authenticated' message, set the auth error, and
//      RETURN (no service / network call).
//   4. `setIsCreating(true)` then `setError('')`.
//   5. try: assemble via `buildTripData(formData, userId, flights)` (called with
//      the DEFAULT now/id factory — 3 args — so runtime entropy matches the
//      source), `await enhanceTripWithImmediateFlightStatus(...)`, then a single
//      `authFetch` POST to `${API_BASE_URL}/trips` with the enhanced body. A
//      non-ok response throws via `parseApiError(response)` (moved verbatim from
//      the source). Then `await response.json()`.
//   6. On success: reset the form to `getEmptyForm()`, empty `flights`, close the
//      lookup sub-form (NOT clearing error — matching the source's success path),
//      resolve `savedTrip = result.trip || tripData`, fire one monitor `/refresh`
//      per saved flight (body from `buildFlightMonitorRequest`, `null` skipped,
//      each `.catch(() => {})`), set `createdTrip`, and open QuickSetup.
//   7. catch: `console.error('Error creating trip:', err)` and surface
//      `err.message || 'Failed to create trip. Please try again.'`.
//   8. finally: `setIsCreating(false)`.
//
// Relative call order is preserved: enhance → trips POST → refresh(s). The
// per-flight monitor iteration mirrors the source's `triggerFlightMonitors`
// (skip when `savedTrip.flights` is empty; skip a flight whose request body is
// `null`).
//
// `finishQuickSetup()` closes QuickSetup and, when provided, invokes
// `onTripCreated(createdTrip)` — the post-create handoff, fired only after the
// user completes/dismisses Quick Setup, exactly as the source did.
//
// The form/flight setters (`setError`, `setFormData`, `setFlights`,
// `setShowFlightLookup`) are injected from `useTripDraft` so this hook drives the
// same state the source's shared component closure did. The container (task 14.7)
// wires the two hooks together.
//
// `use`-prefixed, contains no JSX and performs no rendering.
//
// Validates: Requirements 3.2, 3.4, 1.4

import { useState } from 'react';
import { API_BASE_URL, FLIGHT_MONITOR_API_BASE } from '../../../config/environment';
import { authFetch } from '../../../utils/authFetch';
import { enhanceTripWithImmediateFlightStatus } from '../../../services/immediateFlightStatusService.js';
import { getUserId } from '../../../utils/getUserId';
import buildTripData from '../logic/buildTripData.js';
import buildFlightMonitorRequest from '../logic/buildFlightMonitorRequest.js';
import getEmptyForm from '../logic/getEmptyForm.js';
import validateForm from '../logic/validateForm.js';

// Helper: parse API error response (moved verbatim from ManualTripCreator.js).
async function parseApiError(response) {
  const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
  console.error('🔴 API Error Response:', errorData);
  return new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
}

/**
 * Owns the manual-trip create/submit flow and the post-create QuickSetup handoff.
 *
 * @param {Object} params
 * @param {Object} params.formData - The current form values (from useTripDraft).
 * @param {Array<Object>} params.flights - The added flights (from useTripDraft).
 * @param {Object} params.user - The authenticated user object.
 * @param {Function} params.setError - Setter to surface the error string.
 * @param {Function} params.setFormData - Setter to reset the form on success.
 * @param {Function} params.setFlights - Setter to clear flights on success.
 * @param {Function} params.setShowFlightLookup - Setter to close the lookup sub-form.
 * @param {(trip: Object) => void} [params.onTripCreated] - Parent callback fired
 *   with the saved trip after Quick Setup completes/dismisses.
 * @returns {{
 *   isCreating: boolean,
 *   showQuickSetup: boolean,
 *   createdTrip: Object|null,
 *   handleSubmit: (e: Object) => Promise<void>,
 *   finishQuickSetup: () => void
 * }} Create-flow state and actions.
 */
export function useCreateTrip({
  formData,
  flights,
  user,
  setError,
  setFormData,
  setFlights,
  setShowFlightLookup,
  onTripCreated
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [createdTrip, setCreatedTrip] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm(formData);
    if (validationError) {
      setError(validationError);
      return;
    }

    const userId = getUserId(user);
    if (!userId) {
      console.error('❌ User not authenticated:', { user });
      setError('User not authenticated. Please try logging out and logging back in.');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const tripData = buildTripData(formData, userId, flights);
      const enhancedTripData = await enhanceTripWithImmediateFlightStatus(tripData);

      const response = await authFetch(`${API_BASE_URL}/trips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(enhancedTripData)
      });

      if (!response.ok) {
        throw await parseApiError(response);
      }

      const result = await response.json();

      setFormData(getEmptyForm());
      setFlights([]);
      setShowFlightLookup(false);

      const savedTrip = result.trip || tripData;
      if (savedTrip.flights && savedTrip.flights.length > 0) {
        savedTrip.flights.forEach((f) => {
          const body = buildFlightMonitorRequest(savedTrip, f);
          if (!body) return;
          authFetch(`${FLIGHT_MONITOR_API_BASE}/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          }).catch(() => {});
        });
      }

      setCreatedTrip(savedTrip);
      setShowQuickSetup(true);
    } catch (err) {
      console.error('Error creating trip:', err);
      setError(err.message || 'Failed to create trip. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const finishQuickSetup = () => {
    setShowQuickSetup(false);
    if (onTripCreated) {
      onTripCreated(createdTrip);
    }
  };

  return { isCreating, showQuickSetup, createdTrip, handleSubmit, finishQuickSetup };
}

export default useCreateTrip;
